import React, { useEffect, useLayoutEffect } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';

import App from './App';
import { Toaster } from './components/ui/toaster';
import { getEnv } from './lib/env';
import { useAuthStore } from './stores/auth.store';
import { useBrandingStore } from './stores/branding.store';
import { useFeaturesStore } from './stores/features.store';
import './index.css';

const runtimeTitle = getEnv('VITE_APP_TITLE');
if (runtimeTitle) {
  document.title = runtimeTitle;
}

const AuthInitializer = ({ children }: { children: React.ReactNode }) => {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const loadBranding = useBrandingStore((state) => state.loadBranding);
  const brandingLoaded = useBrandingStore((state) => state.isLoaded);
  const loadFeatures = useFeaturesStore((state) => state.loadFeatures);
  const featuresLoaded = useFeaturesStore((state) => state.isLoaded);

  useEffect(() => {
    checkAuth();
    loadBranding();
    loadFeatures();
  }, [checkAuth, loadBranding, loadFeatures]);

  // Apply branding CSS vars synchronously before browser paint
  useLayoutEffect(() => {
    if (brandingLoaded) {
      applyBrandingFromStore();
    }
  }, [brandingLoaded]);

  if (!brandingLoaded || !featuresLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-600" />
      </div>
    );
  }

  return (
    <>
      {children}
      <Toaster />
    </>
  );
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      // Client/permission errors (4xx) never succeed on retry — retrying only
      // duplicates the error toast the axios interceptor raises.
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Convert hex to OKLCH format for Tailwind v4
// Implements proper RGB → Linear RGB → XYZ → OKLab → OKLCH conversion
function hexToOklch(hex: string): string {
  // Remove # if present and validate
  hex = hex.replace('#', '');
  if (hex.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(hex)) {
    console.warn(`Invalid hex color: ${hex}, using fallback`);
    return 'oklch(0.5 0 0)'; // Fallback to gray
  }

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Convert RGB to linear RGB (sRGB → Linear RGB)
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const rLin = toLinear(r);
  const gLin = toLinear(g);
  const bLin = toLinear(b);

  // Convert Linear RGB to XYZ (D65 illuminant)
  const x = 0.4124564 * rLin + 0.3575761 * gLin + 0.1804375 * bLin;
  const y = 0.2126729 * rLin + 0.7151522 * gLin + 0.072175 * bLin;
  const z = 0.0193339 * rLin + 0.119192 * gLin + 0.9503041 * bLin;

  // Convert XYZ to OKLab using proper transformation matrix
  const l = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
  const m = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
  const s = 0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z;

  // Apply nonlinear transformation
  const lCube = Math.cbrt(l);
  const mCube = Math.cbrt(m);
  const sCube = Math.cbrt(s);

  // Calculate OKLab values
  const L = 0.2104542553 * lCube + 0.793617785 * mCube - 0.0040720468 * sCube;
  const a = 1.9779984951 * lCube - 2.428592205 * mCube + 0.4505937099 * sCube;
  const bVal = 0.0259040371 * lCube + 0.7827717662 * mCube - 0.808675766 * sCube;

  // Convert OKLab to OKLCH
  const C = Math.sqrt(a * a + bVal * bVal);
  let H = Math.atan2(bVal, a) * (180 / Math.PI);
  if (H < 0) H += 360;

  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;
}

// Convert hex to OKLCH with a custom lightness value (for dark-mode chart visibility)
function hexToOklchWithLightness(hex: string, targetL: number): string {
  const base = hexToOklch(hex);
  // Parse the oklch(L C H) string and replace lightness
  const match = base.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
  if (!match) return base;
  return `oklch(${targetL.toFixed(3)} ${match[2]} ${match[3]})`;
}

// Apply theme colors, title, and favicon from branding values
function applyBranding(branding: {
  companyName: string;
  faviconUrl: string;
  primaryColor: string;
  primaryColorDark: string;
}) {
  // Set favicon dynamically
  if (branding.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }

  // Set primary colors for light mode
  if (branding.primaryColor?.startsWith('#')) {
    const root = document.documentElement;
    const oklchColor = hexToOklch(branding.primaryColor);

    root.style.setProperty('--primary', oklchColor);
    root.style.setProperty('--ring', oklchColor);
    root.style.setProperty('--sidebar-primary', oklchColor);
    root.style.setProperty('--sidebar-ring', oklchColor);
    root.style.setProperty('--chart-1', oklchColor);
  }

  // Set primary colors for dark mode via CSS
  const cleanDarkColor = branding.primaryColorDark?.replace(/^['"]|['"]$/g, '');
  if (cleanDarkColor?.startsWith('#')) {
    const oklchColorDark = hexToOklch(cleanDarkColor);

    let darkStyleEl = document.getElementById('dark-mode-primary-override');
    if (!darkStyleEl) {
      darkStyleEl = document.createElement('style');
      darkStyleEl.id = 'dark-mode-primary-override';
      document.head.appendChild(darkStyleEl);
    }
    // Derive a lightened variant of the dark primary for chart visibility
    const chartOklch = hexToOklchWithLightness(cleanDarkColor, 0.65);

    darkStyleEl.textContent = `
      :root.dark {
        --primary: ${oklchColorDark} !important;
        --ring: ${oklchColorDark} !important;
        --sidebar-primary: ${oklchColorDark} !important;
        --sidebar-ring: ${oklchColorDark} !important;
        --chart-1: ${chartOklch} !important;
      }
    `;
  }
}

// Apply branding from store after API fetch completes
function applyBrandingFromStore() {
  const state = useBrandingStore.getState();
  applyBranding({
    companyName: state.companyName,
    faviconUrl: state.faviconUrl,
    primaryColor: state.primaryColor,
    primaryColorDark: state.primaryColorDark,
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthInitializer>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthInitializer>
    </QueryClientProvider>
  </React.StrictMode>,
);
